import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["products", "packs"];
import PortalIdParser from 'PortalIdParser';
import enviro from 'enviro';
import { stringify } from 'hub-http/helpers/params';
import { buildUrl, parseUrl } from 'hub-http/helpers/url';
import { getFullUrl } from 'hubspot-url-utils';
import devLogger from 'react-utils/devLogger';
import { PROD, QA } from 'self-service-api/constants/Environments';
export const createExternalUrl = (path = '', query, subdomain = 'app') => {
  const {
    protocol,
    hostname
  } = parseUrl(getFullUrl(subdomain));
  const urlOptions = {
    protocol,
    hostname,
    path,
    query: stringify(query || {})
  };
  return buildUrl(urlOptions);
};
export const getInterstitialUrl = (query = {}, location = window.location) => {
  const isLocal = !enviro.deployed() && location.hostname.startsWith('local.');
  const isOnPricingPage = location.pathname.includes('pricing');
  const shouldUseLocal = isLocal && isOnPricingPage;
  const url = createExternalUrl(`/pricing/${PortalIdParser.get()}/interstitial`, query, shouldUseLocal ? 'local' : 'app');
  return url;
};
export const getInterstitialV2Url = (purchaseMotion, query = {}, location = window.location) => {
  const isLocal = !enviro.deployed() && location.hostname.startsWith('local.');
  const isOnPricingPage = location.pathname.includes('pricing');
  const shouldUseLocal = isLocal && isOnPricingPage;
  switch (purchaseMotion) {
    case 'BUY_ADDITIONAL_SEATS':
      return createExternalUrl(`/pricing-interstitial/${PortalIdParser.get()}/seats`, query, shouldUseLocal ? 'local' : 'app');
    case 'BUY_ADDITIONAL_CONTACTS':
      return createExternalUrl(`/pricing-interstitial/${PortalIdParser.get()}/contacts`, query, shouldUseLocal ? 'local' : 'app');
    case 'BUY_TOUCHLESS':
      return createExternalUrl(`/pricing-interstitial/${PortalIdParser.get()}/purchase`, query, shouldUseLocal ? 'local' : 'app');
    default:
      throw new Error(`Unsupported purchase motion: ${purchaseMotion}`);
  }
};
export const getCheckoutUrl = (purchaseOrderId, query = {}) => {
  // Allows redirect to `local.hubspotqa` if environment is local, user is on pricing page, and localStorage set.
  const isLocal = !enviro.deployed() && window.location.hostname.startsWith('local.');
  const isOnPricingPage = window.location.pathname.includes('pricing');
  let forceLocalRedirect = false;
  try {
    forceLocalRedirect = localStorage.getItem('CHECKOUT_REDIRECT_TO_LOCAL') === 'true';
  } catch (err) {
    forceLocalRedirect = false;
  }
  const shouldUseLocal = isLocal && isOnPricingPage && forceLocalRedirect;
  const url = createExternalUrl(`/checkout/${PortalIdParser.get()}/purchases/${purchaseOrderId}`, query, shouldUseLocal ? 'local' : 'app');
  return url;
};
const baseUrlOptions = () => {
  const isQAProxy = window.location.hostname.indexOf('wthubspot') >= 0;
  const isQA = enviro.isQa() || isQAProxy;
  const baseUrl = getFullUrl('app', {
    envOverride: isQA ? QA : PROD
  });
  const {
    protocol,
    hostname
  } = parseUrl(baseUrl);
  return {
    protocol,
    hostname
  };
};
const _createPricingPageLink = (path = '', params, hash, isBeta = false) => {
  const portalId = PortalIdParser.get();
  if (params && !params.upgradeSource) {
    devLogger.warn({
      message: `createPricingPageLink: Expected upgradeSource for path ${path} but got ${params.upgradeSource}`,
      key: `createPricingPageLink: ${path}`
    });
  }
  const urlOptions = Object.assign({}, baseUrlOptions(), {
    path: `/${portalId ? 'pricing' : 'public-pricing'}${isBeta ? '-beta' : ''}/${portalId ? `${portalId}/` : ''}${path}`,
    query: params && stringify(params),
    hash: hash && hash.anchorLink ? hash.anchorLink : ''
  });
  return buildUrl(urlOptions);
};
export const navigateTo = (url, openInNewTab = false) => {
  if (window.hubspot.wordpress) {
    window.open(url, '_top');
  } else if (openInNewTab) {
    window.open(url, '_blank');
  } else {
    window.location.href = url;
  }
};
export const getSupportUrl = () => {
  return createExternalUrl(`/support-inbox/${PortalIdParser.get()}?source=in-app-help`);
};
export const getAccountDashboardUrl = () => createExternalUrl(`/account-and-billing/${PortalIdParser.get()}`);
export const getTrialUpgradePageUrl = apiName => {
  return buildUrl({
    path: `/trial-pricing/${PortalIdParser.get()}/upgrade`,
    query: `apiName=${apiName}`
  });
};
export const getUpgradePageUrl = (upgradeSource, params) => {
  return _createPricingPageLink(`upgrade`, Object.assign({
    upgradeSource
  }, params));
};
export function buildInterstitialUrl({
  shouldUseV2Interstitial,
  purchaseMotion,
  apiName,
  purchaseRequest,
  trackingProperties,
  queryParams,
  productsAsQueryParams,
  interstitialQueryParams
}) {
  var _ref2;
  if (shouldUseV2Interstitial) {
    var _ref;
    const remainingQueryParams = _objectWithoutPropertiesLoose(queryParams, _excluded);
    return getInterstitialV2Url(purchaseMotion, Object.assign({
      primaryProduct: apiName
    }, productsAsQueryParams, purchaseRequest.paymentTerm ? {
      paymentTerm: String(purchaseRequest.paymentTerm).toLowerCase()
    } : {}, purchaseRequest.commitmentTerm ? {
      commitmentTerm: String(purchaseRequest.commitmentTerm).toLowerCase()
    } : {}, trackingProperties !== null && trackingProperties !== void 0 && trackingProperties.upgradeSource ? {
      upgradeSource: String(trackingProperties.upgradeSource)
    } : {}, {
      purchaseMotion
    }, remainingQueryParams, purchaseRequest.currencyCode ? {
      currencyCode: String(purchaseRequest.currencyCode)
    } : {}, (_ref = interstitialQueryParams) !== null && _ref !== void 0 ? _ref : {}));
  }
  return getInterstitialUrl(Object.assign({
    upgradeSource: trackingProperties !== null && trackingProperties !== void 0 && trackingProperties.upgradeSource ? String(trackingProperties.upgradeSource) : '',
    purchaseMotion
  }, queryParams, productsAsQueryParams, purchaseRequest.currencyCode ? {
    currencyCode: String(purchaseRequest.currencyCode)
  } : {}, purchaseRequest.paymentTerm ? {
    term: String(purchaseRequest.paymentTerm).toLowerCase()
  } : {}, (_ref2 = interstitialQueryParams) !== null && _ref2 !== void 0 ? _ref2 : {}));
}